import _ from 'lodash';
import axios from 'axios';
import moment from 'moment';

import { rssDiag, serializeAxiosError, summarizeRemoteProxyPrefix } from '../rssDiag';

const xmlParser = new DOMParser();

/**
 * RSS feeds are cross-origin; browsers cannot fetch them directly.
 * - Development: same-origin GET /rss-proxy?url=... (see src/setupProxy.js).
 * - Production: set REACT_APP_RSS_CORS_PROXY to your HTTPS proxy prefix (ends with ?url=), e.g.
 *   https://thesneakercrush.com/rss-proxy?url= — run server/rss-proxy.js behind nginx (see that file).
 * REACT_APP_RSS_CORS_PROXY is only used in production builds; in development the CRA
 * /rss-proxy (setupProxy.js) is always used.
 */
const getRemoteProxyPrefix = () => {
  try {
    const p = typeof process !== 'undefined' && process.env && process.env.REACT_APP_RSS_CORS_PROXY;
    if (p && typeof p === 'string' && p.length) return p;
  } catch (_) {}
  return null;
};

const useDevServerProxy = () => {
  try {
    return typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'development';
  } catch (_) {
    return false;
  }
};

const customFeedUrls = {
  "solecollector.com": "https://solecollector.com/feeds/generator/e/r/1.rss",
  "complex.com": "http://assets.complex.com/feeds/channels/sneakers.xml",
  // /feed 403s with HTML; /feed/ serves RSS (WordPress + CDN rules).
  "sneakernews.com": "https://sneakernews.com/feed/",
}

/**
 * Default WordPress-style feed path. Many sites only serve RSS at .../feed/ (trailing slash);
 * .../feed without slash returns 403 or HTML (e.g. Sneaker News).
 */
const getFeedUrl = (url) => {
  const trimmed = _.trimEnd(url, '/');
  for (const host in customFeedUrls) {
    if (trimmed.includes(host)) return customFeedUrls[host];
  }
  const lower = trimmed.toLowerCase();
  if (lower.endsWith("/feed")) {
    return `${trimmed}/`;
  }
  return `${trimmed}/feed/`;
}

const getTagText = (node, tag) => {
  if (!node || !node.getElementsByTagName) return undefined;
  const element = node.getElementsByTagName(tag)[0];
  if (!element) return undefined;
  const text = element.textContent != null ? element.textContent.trim() : '';
  if (text) return text;
  const childNode = element.childNodes[0];
  return childNode && childNode.nodeValue;
}

/** True when the body is an HTML document, not RSS/XML (common when feeds are bot-blocked). */
const looksLikeHtmlPage = (data) => {
  const head = data.slice(0, 8192);
  if (/^\s*<\?xml/i.test(data) && /<rss[\s>]/i.test(head)) return false;
  if (/^\s*<rss[\s>]/i.test(data.trim())) return false;
  if (/<!DOCTYPE\s+html/i.test(head)) return true;
  if (/<html[\s>]/i.test(head) && !/<rss[\s>]/i.test(head)) return true;
  return false;
};

const decodeXmlishText = (raw) => {
  if (!raw) return undefined;
  return raw
    .replace(/<!\[CDATA\[([\s\S]*?)\]\]>/gi, '$1')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .trim();
};

const extractFirstTag = (block, localName) => {
  const escaped = localName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(':', '\\:');
  const re = new RegExp(`<${escaped}[^>]*>([\\s\\S]*?)<\\/${escaped}>`, 'i');
  const m = block.match(re);
  return m ? decodeXmlishText(m[1]) : undefined;
};

/** When DOMParser rejects markup (bad entities, minor tag issues), try RSS 2.0 regex extraction. */
const parseRss2Lenient = (data) => {
  const channelMatch = data.match(/<channel[^>]*>([\s\S]*?)<\/channel>/i);
  if (!channelMatch) return [];
  const channelXml = channelMatch[1];
  const source = extractFirstTag(channelXml, 'title') || '';
  const items = [];
  const itemRe = /<item\b[^>]*>([\s\S]*?)<\/item>/gi;
  let m;
  while ((m = itemRe.exec(channelXml)) !== null) {
    const block = m[1];
    const title = extractFirstTag(block, 'title');
    const link = extractFirstTag(block, 'link');
    const pubDate = extractFirstTag(block, 'pubDate');
    const content =
      extractFirstTag(block, 'content:encoded') ||
      extractFirstTag(block, 'description');
    let image;
    const mediaImg = block.match(
      /<media:content[^>]*medium=["']image["'][^>]*url=["']([^"']+)["']/i,
    );
    if (mediaImg) image = mediaImg[1];
    items.push({
      title,
      link,
      date: moment(pubDate).format(),
      source,
      content,
      image,
      feedItem: null,
    });
  }
  return items;
};

export const getMediaContents = (node, type) => {
  if (!node || !node.getElementsByTagName) return [];
  return Array.from(node.getElementsByTagName("media:content"))
    .filter(media => media.getAttribute("medium") === type)
    .map(media => media.getAttributeNode("url").nodeValue);
}

const parseResponse = (data, feedUrl) => {
  if (typeof data !== 'string' || !data.trim()) {
    rssDiag('warn', 'parse_empty_body', { feedUrl });
    return [];
  }
  if (looksLikeHtmlPage(data)) {
    rssDiag('warn', 'parse_body_not_rss_looks_like_html', {
      feedUrl,
      bodyPreview: data.slice(0, 200),
    });
    return [];
  }
  const doc = xmlParser.parseFromString(data, "text/xml");
  const parserError = doc.getElementsByTagName("parsererror")[0];
  const channel = doc.getElementsByTagName("channel")[0];
  if (parserError || !channel) {
    const lenient = parseRss2Lenient(data);
    if (lenient.length) {
      return lenient;
    }
    if (parserError) {
      rssDiag('warn', 'parse_xml_dom_error', {
        feedUrl,
        detail: (parserError.textContent || '').slice(0, 400),
      });
    } else {
      rssDiag('warn', 'parse_no_channel', { feedUrl });
    }
    return [];
  }
  const source = getTagText(channel, 'title');

  return Array.from(channel.getElementsByTagName("item")).map((item) => ({
    title: getTagText(item, 'title'),
    link: getTagText(item, 'link'),
    date: moment(getTagText(item, 'pubDate')).format(),
    source,
    content: getTagText(item, 'content:encoded'),
    image: getMediaContents(item, 'image')[0],
    feedItem: item,
  }));
}

const fetchFeed = (url) => {
  const feedUrl = getFeedUrl(url);
  const remotePrefix = getRemoteProxyPrefix();
  const axiosConfig = {
    method: 'GET',
    responseType: 'text',
    timeout: 90000,
    validateStatus: () => true,
    headers: {
      Accept: 'application/rss+xml, application/xml, text/xml, */*',
    },
    transformResponse: [(data) => parseResponse(data, feedUrl)],
  };

  // Do not set Referer here: browsers refuse it on XHR ("unsafe header"). Proxies add Referer server-side.
  axiosConfig.headers['Accept-Language'] = 'en-US,en;q=0.9';

  const logOk = (mode, res) => {
    const itemCount = Array.isArray(res.data) ? res.data.length : null;
    if (res.status >= 400) {
      rssDiag('warn', 'feed_http_non_success_status', {
        mode,
        feedUrl,
        sourceBaseUrl: url,
        httpStatus: res.status,
      });
    }
    rssDiag('info', 'feed_http_ok', {
      mode,
      sourceBaseUrl: url,
      feedUrl,
      httpStatus: res.status,
      itemCount,
    });
    if (itemCount === 0) {
      rssDiag('warn', 'feed_zero_items_after_parse', { mode, feedUrl, sourceBaseUrl: url });
    }
  };

  const logFail = (mode, err) => {
    rssDiag('error', 'feed_http_failed', {
      mode,
      sourceBaseUrl: url,
      feedUrl,
      remoteProxyHost: mode === 'remote' ? summarizeRemoteProxyPrefix(remotePrefix) : undefined,
      hint:
        mode === 'direct-unproxied'
          ? 'Production needs REACT_APP_RSS_CORS_PROXY or feeds hit CORS/mixed-content.'
          : mode === 'remote'
            ? 'Check REACT_APP_RSS_CORS_PROXY URL and that server/rss-proxy.js is running behind nginx.'
            : undefined,
      ...serializeAxiosError(err),
    });
  };

  // Dev must use same-origin /rss-proxy even if .env.build sets REACT_APP_RSS_CORS_PROXY for production.
  if (useDevServerProxy()) {
    return axios({
      ...axiosConfig,
      url: '/rss-proxy',
      params: { url: feedUrl },
    })
      .then((res) => {
        logOk('dev-proxy', res);
        return res.data;
      })
      .catch((err) => {
        logFail('dev-proxy', err);
        throw err;
      });
  }

  if (remotePrefix) {
    rssDiag('info', 'feed_using_remote_proxy', {
      feedUrl,
      proxyHost: summarizeRemoteProxyPrefix(remotePrefix),
    });
    return axios({
      ...axiosConfig,
      url: remotePrefix + encodeURIComponent(feedUrl),
    })
      .then((res) => {
        logOk('remote', res);
        return res.data;
      })
      .catch((err) => {
        logFail('remote', err);
        throw err;
      });
  }

  rssDiag('warn', 'feed_no_proxy_configured', {
    feedUrl,
    sourceBaseUrl: url,
    hint: 'Set REACT_APP_RSS_CORS_PROXY in .env.build for production builds.',
  });
  return axios({
    ...axiosConfig,
    url: feedUrl,
  })
    .then((res) => {
      logOk('direct-unproxied', res);
      return res.data;
    })
    .catch((err) => {
      logFail('direct-unproxied', err);
      throw err;
    });
}

export default fetchFeed;
